/**
 * @file useWechatOrderPay.js
 * @description 通用微信订单：预下单 → NATIVE 扫码 / JSAPI / H5 → 轮询
 * @module shared/composables/useWechatOrderPay
 */

import { ref } from 'vue'
import QRCode from 'qrcode'
import { getPayScene, toWechatPayScene } from '@/shared/utils/platform'
import { invokeWeChatPay } from '@/shared/utils/wechatPay'
import { ERR_WX_MP_OAUTH_REQUIRED } from '@/shared/utils/apiErrorCodes'
import { handleWxMpOAuthRequiredResponse } from '@/shared/utils/wxMpOAuth'
import { isWechatOrderClosed, isWechatOrderPaid } from '@/shared/utils/payOrderStatus'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

/**
 * 购卷充值：移动端外置浏览器走 H5，其余与打赏一致
 * @param {import('@/shared/utils/platform').PayScene} scene
 * @returns {'NATIVE'|'JSAPI'|'H5'}
 */
export function toGoldWechatPayScene(scene) {
  if (scene === 'PC_NATIVE') return 'NATIVE'
  if (scene === 'MOBILE_GUIDE') return 'H5'
  return toWechatPayScene(scene)
}

/**
 * @param {{
 *   prepareFn: (payload: object) => Promise<object>,
 *   queryFn: (payload: { orderId: string }) => Promise<object>,
 *   buildPreparePayload: (scene: string, idempotencyKey: string) => object,
 *   onBeforeJsapiPrepare?: () => void,
 *   onPrepareSuccess?: () => void,
 *   onPaid?: (vo: object) => void,
 *   resolveError?: (err: unknown) => string,
 *   isOAuthError?: (err: unknown) => boolean,
 *   getOAuthUrl?: (err: unknown) => string,
 * }} hooks
 */
export function createWechatOrderPay(hooks) {
  const qrDataUrl = ref('')
  const loading = ref(false)
  const errorMessage = ref('')
  const currentOrderId = ref('')
  const currentOutTradeNo = ref('')
  const isConfirmingPayment = ref(false)
  const payStep = ref(/** @type {'select'|'qrcode'|'success'} */ ('select'))

  let pollTimer = null
  let pollStartedAt = 0
  let onVisibilityRefresh = null
  let pollConsecutiveFailures = 0
  let submitInFlight = false

  /**
   * @param {unknown} err
   * @returns {string}
   */
  function resolveError(err) {
    if (hooks.resolveError) return hooks.resolveError(err)
    if (err instanceof Error && err.message) return err.message
    return '操作失败，请稍后再试'
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    if (onVisibilityRefresh && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityRefresh)
      onVisibilityRefresh = null
    }
    isConfirmingPayment.value = false
  }

  function resetPayState() {
    stopPolling()
    qrDataUrl.value = ''
    loading.value = false
    errorMessage.value = ''
    currentOrderId.value = ''
    currentOutTradeNo.value = ''
    isConfirmingPayment.value = false
    payStep.value = 'select'
  }

  /**
   * @param {string} codeUrl
   */
  async function renderQrCode(codeUrl) {
    qrDataUrl.value = await QRCode.toDataURL(codeUrl, {
      width: 200,
      margin: 2,
      errorCorrectionLevel: 'M',
    })
  }

  /**
   * @returns {Promise<boolean>}
   */
  async function pollOrderStatusOnce() {
    if (!currentOrderId.value) return false
    try {
      const statusVo = await hooks.queryFn({ orderId: currentOrderId.value })
      pollConsecutiveFailures = 0
      if (statusVo?.orderId) currentOrderId.value = String(statusVo.orderId)
      if (statusVo?.outTradeNo) currentOutTradeNo.value = String(statusVo.outTradeNo)
      if (isWechatOrderPaid(statusVo)) {
        stopPolling()
        loading.value = false
        payStep.value = 'success'
        hooks.onPaid?.(statusVo)
        return true
      }
      if (isWechatOrderClosed(statusVo)) {
        stopPolling()
        loading.value = false
        errorMessage.value = '支付失败或订单已关闭，请重试'
        payStep.value = 'select'
        return false
      }
    } catch (err) {
      pollConsecutiveFailures += 1
      if (pollConsecutiveFailures >= 3 && loading.value) {
        errorMessage.value = resolveError(err) || '查询支付结果失败，请稍后再试'
      }
    }
    return false
  }

  /**
   * @param {string} orderId
   * @param {string} [outTradeNo]
   */
  function startOrderPolling(orderId, outTradeNo = '') {
    stopPolling()
    currentOrderId.value = orderId ? String(orderId) : ''
    currentOutTradeNo.value = outTradeNo ? String(outTradeNo) : ''
    pollStartedAt = Date.now()
    pollConsecutiveFailures = 0
    void pollOrderStatusOnce()
    pollTimer = setInterval(async () => {
      if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
        stopPolling()
        loading.value = false
        errorMessage.value = '支付确认超时，若已付款请稍后查看余额'
        return
      }
      await pollOrderStatusOnce()
    }, POLL_INTERVAL_MS)
    if (typeof document !== 'undefined') {
      onVisibilityRefresh = () => {
        if (document.visibilityState === 'visible') void pollOrderStatusOnce()
      }
      document.addEventListener('visibilitychange', onVisibilityRefresh)
    }
  }

  async function confirmPaymentManually() {
    if (!currentOrderId.value) return
    if (isConfirmingPayment.value) return
    isConfirmingPayment.value = true
    errorMessage.value = ''
    try {
      const paid = await pollOrderStatusOnce()
      if (!paid && payStep.value === 'qrcode') {
        errorMessage.value = '暂未查询到支付结果，请稍后再试'
      }
    } finally {
      if (payStep.value === 'qrcode') isConfirmingPayment.value = false
    }
  }

  /**
   * @param {string} idempotencyKey
   * @returns {Promise<boolean>} 是否已发起支付（OAuth 整页跳转时为 false）
   */
  async function startPay(idempotencyKey) {
    if (submitInFlight || loading.value) return false
    submitInFlight = true
    errorMessage.value = ''
    loading.value = true
    try {
      const clientScene = getPayScene()
      const apiScene = toGoldWechatPayScene(clientScene)
      if (apiScene === 'JSAPI') hooks.onBeforeJsapiPrepare?.()
      const order = await hooks.prepareFn(hooks.buildPreparePayload(apiScene, idempotencyKey))
      hooks.onPrepareSuccess?.()
      currentOrderId.value = order.orderId ? String(order.orderId) : ''
      currentOutTradeNo.value = order.outTradeNo ? String(order.outTradeNo) : ''
      let keepLoadingForPoll = false

      if (apiScene === 'NATIVE') {
        if (!order.codeUrl) throw new Error('未获取到支付二维码，请稍后再试')
        await renderQrCode(order.codeUrl)
        payStep.value = 'qrcode'
        if (order.orderId) startOrderPolling(String(order.orderId), order.outTradeNo || '')
      } else if (apiScene === 'H5') {
        if (!order.h5Url) throw new Error('未获取到支付链接，请稍后再试')
        if (order.orderId) startOrderPolling(String(order.orderId), order.outTradeNo || '')
        window.location.assign(order.h5Url)
        keepLoadingForPoll = true
      } else if (apiScene === 'JSAPI') {
        if (!order.jsapi) throw new Error('未获取到微信支付参数')
        await invokeWeChatPay({
          appId: order.jsapi.appId,
          timeStamp: order.jsapi.timeStamp,
          nonceStr: order.jsapi.nonceStr,
          package: order.jsapi.package,
          signType: order.jsapi.signType,
          paySign: order.jsapi.paySign,
        })
        if (order.orderId) {
          keepLoadingForPoll = true
          startOrderPolling(String(order.orderId), order.outTradeNo || '')
          await pollOrderStatusOnce()
          await pollOrderStatusOnce()
        }
      }

      if (!keepLoadingForPoll) loading.value = false
      return true
    } catch (err) {
      loading.value = false
      if (err instanceof Error && err.message === '用户取消支付') {
        errorMessage.value = '已取消支付'
        hooks.onPrepareSuccess?.()
        return false
      }
      if (hooks.isOAuthError?.(err)) {
        const redirected = handleWxMpOAuthRequiredResponse({
          code: ERR_WX_MP_OAUTH_REQUIRED,
          data: { oauthAuthorizeUrl: hooks.getOAuthUrl?.(err) || '' },
        })
        if (!redirected) {
          errorMessage.value = '需要微信授权，请重试'
          hooks.onPrepareSuccess?.()
        }
        return false
      }
      hooks.onPrepareSuccess?.()
      errorMessage.value = resolveError(err)
      return false
    } finally {
      submitInFlight = false
    }
  }

  return {
    qrDataUrl,
    loading,
    errorMessage,
    currentOrderId,
    currentOutTradeNo,
    isConfirmingPayment,
    payStep,
    startPay,
    stopPolling,
    resetPayState,
    confirmPaymentManually,
    startOrderPolling,
  }
}
